import { Box } from '@mui/material';
import {
  forwardRef,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type CanvasHTMLAttributes,
} from 'react';

import {
  useBoardDrawingStore,
  useBoardSelectionStore,
  useWallParticipantsStore,
  useWallContentStore,
  useWorkspaceUiStore,
  type WallBoard,
} from '@walls/store';
import { useCanvasRenderer } from './hooks/useCanvasRenderer';
import { useDrawingPointerEvents } from './hooks/useDrawingPointerEvents';
import { drawingLayerZIndex } from '../positioning/zIndex';
import { TextInputOverlay } from './TextInputOverlay';
import { getShapePermissions } from '../shapes/hooks/useShapePermissions';
import {
  boardRenderOverscan,
  getBoardRenderWindow,
  type BoardRenderWindow,
} from '../viewport/renderWindow';
import { useBoardViewportRef } from '../viewport/BoardViewportContext';

interface DrawingCanvasLayerProps {
  board: WallBoard;
  renderWindow: BoardRenderWindow;
}

const canvasRecenterMargin = boardRenderOverscan / 2;

export function DrawingCanvasLayer({ board, renderWindow }: DrawingCanvasLayerProps) {
  const lockedCanvasRef = useRef<HTMLCanvasElement>(null);
  const activeCanvasRef = useRef<HTMLCanvasElement>(null);
  const viewportRef = useBoardViewportRef();
  const renderWindowAnimationFrameRef = useRef<number | null>(null);
  const [canvasRenderWindow, setCanvasRenderWindow] =
    useState<BoardRenderWindow>(renderWindow);
  const shapes = useWallContentStore((state) => state.shapes);
  const draftShape = useBoardDrawingStore((state) => state.draftShape);
  const localShapes = useBoardDrawingStore((state) => state.localShapes);
  const selectedShapeId = useBoardDrawingStore((state) => state.selectedShapeId);
  const selectedIds = useBoardSelectionStore((state) => state.selectedIds);
  const currentParticipantId = useWallParticipantsStore((state) => state.currentParticipantId);
  const zoom = useWorkspaceUiStore((state) => state.zoom);
  const allShapes = [...shapes, ...localShapes];
  const lockedShapes = allShapes.filter((shape) => shape.isLocked);
  const activeShapes = allShapes.filter((shape) => !shape.isLocked);
  const selectedShapeIds = allShapes
    .filter((shape) => shape.id === selectedShapeId || selectedIds.includes(shape.id))
    .map((shape) => shape.id);
  const editableSelectedShapeIds = allShapes
    .filter(
      (shape) =>
        (shape.id === selectedShapeId || selectedIds.includes(shape.id)) &&
        getShapePermissions(board, shape, currentParticipantId).canResize
    )
    .map((shape) => shape.id);
  const updateCanvasRenderWindow = useCallback(() => {
    const viewport = viewportRef?.current;

    if (!viewport) {
      setCanvasRenderWindow(renderWindow);
      return;
    }

    const visibleWindow = {
      height: viewport.clientHeight,
      width: viewport.clientWidth,
      x: viewport.scrollLeft,
      y: viewport.scrollTop,
    };

    setCanvasRenderWindow((currentRenderWindow) => {
      if (isVisibleWindowStable(currentRenderWindow, visibleWindow)) {
        return currentRenderWindow;
      }

      return getBoardRenderWindow(visibleWindow);
    });
  }, [renderWindow, viewportRef]);
  const scheduleCanvasRenderWindowUpdate = useCallback(() => {
    if (renderWindowAnimationFrameRef.current != null) {
      return;
    }

    renderWindowAnimationFrameRef.current = requestAnimationFrame(() => {
      renderWindowAnimationFrameRef.current = null;
      updateCanvasRenderWindow();
    });
  }, [updateCanvasRenderWindow]);
  const pointerEvents = useDrawingPointerEvents({
    board,
    canvasRef: activeCanvasRef,
    renderOrigin: canvasRenderWindow,
    zoom,
  });

  useLayoutEffect(() => {
    updateCanvasRenderWindow();

    const viewport = viewportRef?.current;

    if (!viewport) {
      return undefined;
    }

    viewport.addEventListener('scroll', scheduleCanvasRenderWindowUpdate, { passive: true });

    return () => {
      if (renderWindowAnimationFrameRef.current != null) {
        cancelAnimationFrame(renderWindowAnimationFrameRef.current);
        renderWindowAnimationFrameRef.current = null;
      }

      viewport.removeEventListener('scroll', scheduleCanvasRenderWindowUpdate);
    };
  }, [scheduleCanvasRenderWindowUpdate, updateCanvasRenderWindow, viewportRef]);

  useCanvasRenderer({
    canvasRef: lockedCanvasRef,
    height: canvasRenderWindow.height,
    editableSelectedShapeIds,
    renderWindow: canvasRenderWindow,
    selectedShapeIds,
    shapes: lockedShapes,
    width: canvasRenderWindow.width,
    zoom,
  });

  useCanvasRenderer({
    canvasRef: activeCanvasRef,
    draftShape,
    editableSelectedShapeIds,
    height: canvasRenderWindow.height,
    renderWindow: canvasRenderWindow,
    selectedShapeIds,
    shapes: activeShapes,
    width: canvasRenderWindow.width,
    zoom,
  });

  return (
    <Box
      sx={{ inset: 0, pointerEvents: 'none', position: 'absolute', zIndex: drawingLayerZIndex }}
    >
      <Canvas
        ref={lockedCanvasRef}
        id="locked-drawing-canvas"
        height={canvasRenderWindow.height}
        width={canvasRenderWindow.width}
        left={canvasRenderWindow.x}
        top={canvasRenderWindow.y}
      />
      <Canvas
        ref={activeCanvasRef}
        id="drawing-canvas"
        height={canvasRenderWindow.height}
        width={canvasRenderWindow.width}
        left={canvasRenderWindow.x}
        top={canvasRenderWindow.y}
        pointerEvents={pointerEvents.drawingEnabled ? 'auto' : 'none'}
        onPointerDown={pointerEvents.onPointerDown}
        onPointerMove={pointerEvents.onPointerMove}
        onPointerUp={pointerEvents.onPointerUp}
        onPointerCancel={pointerEvents.onPointerCancel}
      />
      <TextInputOverlay board={board} />
    </Box>
  );
}

interface CanvasProps extends CanvasHTMLAttributes<HTMLCanvasElement> {
  left: number;
  pointerEvents?: 'auto' | 'none';
  top: number;
}

const Canvas = forwardRef<HTMLCanvasElement, CanvasProps>(function Canvas(
  { height, left, pointerEvents = 'none', style, top, width, ...props },
  ref
) {
  return (
    <canvas
      ref={ref}
      height={height}
      width={width}
      style={{
        display: 'block',
        height,
        left,
        pointerEvents,
        position: 'absolute',
        top,
        width,
        ...style,
      }}
      {...props}
    />
  );
});

function isVisibleWindowStable(
  renderWindow: BoardRenderWindow,
  visibleWindow: { height: number; width: number; x: number; y: number }
) {
  return (
    visibleWindow.x >= renderWindow.x + canvasRecenterMargin &&
    visibleWindow.y >= renderWindow.y + canvasRecenterMargin &&
    visibleWindow.x + visibleWindow.width <=
      renderWindow.x + renderWindow.width - canvasRecenterMargin &&
    visibleWindow.y + visibleWindow.height <=
      renderWindow.y + renderWindow.height - canvasRecenterMargin
  );
}
